import { Metadata, type NumberingPlan, type PhoneNumber } from "libphonenumber-js/max";

// The national destination code, which no port of libphonenumber carries and none needs to: a plan's own formatting
// is where it says which digits are the code, so Google reads it back out of the international format rather than
// writing it down twice, and so does this. Nothing here is a numbering plan of its own — it is the one the library
// already applied, read in the other direction.
export function destinationCode(number: PhoneNumber): string {
  const groups = number.formatInternational().split(NON_DIGITS);
  // The first group is what stood in front of the plus and is empty, and the second is the calling code, so a number
  // written in fewer than four groups has no destination code written into it at all.
  return groups.length <= 3 ? "" : groups[2];
}

// The same digits, given only where they name a place somebody can be. A country with no national prefix dials every
// number in full and has no areas to name — Denmark, Norway, Singapore — unless the plan keeps a leading zero the way
// Italy's does, which is the same thing said the other way. A mobile is in no area at all, outside the four plans
// that put one there.
export function areaCode(number: PhoneNumber): string {
  const type = number.getType();
  if (!type || !GEOGRAPHIC.has(type)) return "";
  if (!nationalPrefix(number) && !number.nationalNumber.startsWith("0")) return "";
  if (type === "MOBILE" && !MOBILE_AREAS.has(number.countryCallingCode)) return "";
  return destinationCode(number);
}

const NON_DIGITS = /\D+/;

// A line that is somewhere: the rest — toll free, premium rate, voicemail, a personal number — is a range and not a
// place, and reads the same dialled from anywhere.
const GEOGRAPHIC = new Set(["FIXED_LINE", "FIXED_LINE_OR_MOBILE", "MOBILE"]);

// libphonenumber's own four, and it is a list because it is a fact about those plans rather than a rule about any:
// Mexico, Argentina, Brazil and Indonesia write a mobile with the area code of where it was issued. China is the one
// plan that files mobiles under an area and still gives them no area code, so it is not here.
const MOBILE_AREAS = new Set(["52", "54", "55", "62"]);

const metadata = new Metadata();

// `nationalPrefix` is on the numbering plan at run time and not in the typings the package publishes, so it is
// reached through the one method this needs rather than a cast of the whole object. It is read from the same
// metadata the formatting above came out of, which is the point: a plan cannot say one thing to the formatter and
// another to this. `tests/phone-number.test.ts` holds Denmark having no area code and Australia having one, which is
// what says so if the method is ever renamed.
function nationalPrefix(number: PhoneNumber): boolean {
  if (!number.country) return false;
  metadata.selectNumberingPlan(number.country);
  const plan = metadata.numberingPlan as (NumberingPlan & Partial<WithNationalPrefix>) | undefined;
  return Boolean(plan?.nationalPrefix?.());
}

interface WithNationalPrefix {
  nationalPrefix(): string | undefined;
}
